import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * On-brand FAQ accordion for /services and /training.
 *
 * Aesthetic: dark industrial, sharp edges, orange operational accents, no rounded
 * corners, no card shadows. Matches the rest of the ForgedOps surface treatment.
 * Single item open at a time (focus discipline). Used in tandem with the
 * FAQPage JSON-LD already injected — when both are present, Google can award
 * FAQ rich results in SERP.
 */
export default function FAQAccordion({
  kicker = "Common Questions",
  title = "Field-Tested Answers.",
  items = [],
  testidPrefix = "faq",
}) {
  const [openIdx, setOpenIdx] = useState(0);
  if (!items.length) return null;

  return (
    <section
      data-testid={`${testidPrefix}-section`}
      className="grain-overlay relative border-b border-zinc-900 bg-black overflow-hidden"
    >
      {/* Caution stripe at very top */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-[repeating-linear-gradient(135deg,#ff6b1a_0_18px,#0a0a0a_18px_30px)] opacity-80" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
        {/* Header */}
        <div className="flex flex-col items-start gap-5 mb-12 md:mb-16 max-w-3xl">
          <div className="flex items-center gap-3">
            <span className="block w-12 h-[3px] bg-[#ff6b1a]" />
            <span
              data-testid={`${testidPrefix}-kicker`}
              className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a] font-bold"
            >
              {kicker}
            </span>
          </div>
          <h2
            data-testid={`${testidPrefix}-headline`}
            className="font-display uppercase font-black tracking-[-0.025em] leading-[0.9] text-balance text-[clamp(2rem,4.4vw,4rem)] metallic-text"
          >
            {title}
          </h2>
        </div>

        {/* Items */}
        <ul className="divide-y divide-zinc-900 border-y border-zinc-900" data-testid={`${testidPrefix}-list`}>
          {items.map((item, i) => {
            const open = i === openIdx;
            const Icon = open ? Minus : Plus;
            return (
              <li
                key={i}
                data-testid={`${testidPrefix}-item-${i}`}
                className="relative"
              >
                {/* Orange left rail when open */}
                <span
                  aria-hidden="true"
                  className={`absolute left-0 top-0 bottom-0 w-[3px] bg-[#ff6b1a] transition-opacity duration-200 ${
                    open ? "opacity-100" : "opacity-0"
                  }`}
                />
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  data-testid={`${testidPrefix}-question-${i}`}
                  className="w-full flex items-start justify-between gap-6 py-7 md:py-8 pl-6 md:pl-8 pr-3 md:pr-4 text-left group focus:outline-none focus-visible:bg-[#0a0a0c]/60 transition-colors"
                >
                  <span className="font-display uppercase tracking-[-0.01em] text-[18px] md:text-[22px] font-bold leading-tight text-white group-hover:text-[#ffa672] transition-colors">
                    {item.question}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`mt-1 flex-shrink-0 flex items-center justify-center w-9 h-9 border ${
                      open
                        ? "border-[#ff6b1a] bg-[#ff6b1a]/15 text-[#ff6b1a]"
                        : "border-zinc-800 text-zinc-500 group-hover:border-[#ff6b1a]/60 group-hover:text-[#ff6b1a]"
                    } transition-all duration-200`}
                  >
                    <Icon size={16} strokeWidth={2.5} />
                  </span>
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p
                      data-testid={`${testidPrefix}-answer-${i}`}
                      className="pb-8 pl-6 md:pl-8 pr-4 md:pr-16 text-zinc-400 text-[15px] md:text-[16px] leading-relaxed max-w-4xl"
                    >
                      {item.answer}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Footer prompt */}
        <div className="mt-12 md:mt-14 flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="font-display uppercase tracking-[0.18em] text-[11px] md:text-[12px] text-zinc-500">
            Still have questions?
          </span>
          <Link
            to="/contact"
            data-testid={`${testidPrefix}-cta-contact`}
            className="font-display uppercase tracking-[0.18em] text-[11px] md:text-[12px] text-[#ff6b1a] hover:text-[#ffa672] transition-colors border-b border-[#ff6b1a]/60 hover:border-[#ffa672] pb-1"
          >
            Talk to an Ops Lead →
          </Link>
        </div>
      </div>
    </section>
  );
}
